import { createEffect, createEvent, createStore, sample } from 'effector';
import {
  addTagToNote,
  createWorkspaceTag,
  CreateWorkspaceTagPayload,
  deleteTagFromNote,
  deleteWorkspaceTag,
  DeleteWorkspaceTagPayload,
  getNoteTags,
  getWorkspaceNotesTags,
  getWorkspaceTags,
  renameWorkspaceTag,
  RenameWorkspaceTagPayload,
} from '@nx-frontend/shared/api/webnotes';
import { $currentWorkspace } from '@nx-frontend/shared/store/current-workspace';
import {
  $menuState,
  createTagMenuItem,
  deleteMenuItemEvent,
  moveMenuItemEvent,
  webnotesAddMenuItemEvent,
} from '@nx-frontend/shared/store/feature-menu-tree';
import {
  AddTagToNotePayload,
  DeleteTagFromNotePayload,
  FetchWorkspaceTagsPayload,
  GetNoteTagsPayload,
  MenuItemType,
  MenuType,
  MenuItem,
  Tag,
  Workspace,
  MenuItemAddPayload,
} from '@nx-frontend/shared/types';
import { generateTagId, tagsComparator } from '@nx-frontend/shared/utils';

export interface WorkspaceTagsState {
  workspaceId: string;
  tags: Tag[];
  isFetching: boolean;
}
const initialWorkspaceTagsState: WorkspaceTagsState = {
  workspaceId: '',
  tags: [],
  isFetching: false,
};

export interface NoteTags {
  noteId: string;
  tags: string[];
  isFetching: boolean;
}
export interface NotesTagsState {
  notes: Record<string, NoteTags>;
  tags: Record<string, string[]>;
}
const initialNotesTagsState: NotesTagsState = {
  notes: {},
  tags: {},
};

export const $notesTags = createStore<NotesTagsState>(initialNotesTagsState);
export const $workspaceTags = createStore<WorkspaceTagsState>(initialWorkspaceTagsState);

export const fetchNoteTagsEvent = createEvent<{ noteId: string }>();
const startFetchingNoteTagsEvent = createEvent<GetNoteTagsPayload>();

export const addTagToNoteEvent = createEvent<{ noteId: string; tag: string }>();
const startAddingTagToNoteEvent = createEvent<AddTagToNotePayload>();

export const deleteTagFromNoteEvent = createEvent<{ noteId: string; tag: string }>();
const startDeletingTagFromNoteEvent = createEvent<DeleteTagFromNotePayload>();

export const fetchWorkspaceNotesTagsEvent = createEvent();
export const fetchWorkspaceTagsEvent = createEvent<FetchWorkspaceTagsPayload>();
export const createWorkspaceTagEvent = createEvent<CreateWorkspaceTagPayload>();
export const deleteWorkspaceTagEvent = createEvent<DeleteWorkspaceTagPayload>();
export const renameWorkspaceTagEvent = createEvent<RenameWorkspaceTagPayload>();

// Sort sidebar items
const sortSidebarTagsItemsEvent = createEvent();
const sortSidebarTagsItemsFx = createEffect((tagMenuItems: MenuItem[]) => {
  tagMenuItems
    .sort(tagsComparator)
    .map((item, index) => ({ ...item, index }))
    .forEach((item) => {
      moveMenuItemEvent({
        menuType: MenuType.WEBNOTES_MENU,
        oldMenuType: MenuType.WEBNOTES_MENU,
        id: item.id,
        parentId: item.parentId,
        index: item.index,
      });
    });

  return;
});
sample({
  clock: sortSidebarTagsItemsEvent,
  source: [$menuState],
  fn: ([menuState]) => {
    const tagItems = Object.values(menuState['webnotes-menu'].entities).filter(
      (item) => item.type === MenuItemType.TAG
    );
    return tagItems;
  },
  target: sortSidebarTagsItemsFx,
});
// Special effect for adding tag to sidebar and triggering sorting
const addTagMenuItemFx = createEffect((payload: MenuItemAddPayload) => {
  webnotesAddMenuItemEvent(payload);
});
sample({
  clock: addTagMenuItemFx.doneData,
  target: sortSidebarTagsItemsEvent,
});

// Fetch note tags
const fetchNoteTagsFx = createEffect(async ({ workspaceId, noteId }: GetNoteTagsPayload) => {
  return {
    noteId,
    tags: (await getNoteTags({ noteId, workspaceId })) ?? [],
  };
});

$notesTags.on(startFetchingNoteTagsEvent, (state, { noteId }) => {
  return {
    ...state,
    notes: {
      ...state.notes,
      [noteId]: {
        ...state.notes[noteId],
        isFetching: true,
      },
    },
  };
});

sample({
  clock: fetchNoteTagsEvent,
  source: [$currentWorkspace, $notesTags],
  fn: (stores, { noteId }) => {
    const workspace = stores[0] as Workspace;
    return { workspaceId: workspace.globalId, noteId };
  },
  filter: (stores, { noteId }) => {
    const notesTags = stores[1] as NotesTagsState;
    return !notesTags.notes[noteId]?.isFetching;
  },
  target: [startFetchingNoteTagsEvent, fetchNoteTagsFx],
});

$notesTags.on(fetchNoteTagsFx.doneData, (state, { noteId, tags }) => {
  return {
    ...state,
    notes: {
      ...state.notes,
      [noteId]: {
        noteId,
        tags,
        isFetching: false,
      },
    },
  };
});

// Create workspace tag
const createWorkspaceTagFx = createEffect(async (params: CreateWorkspaceTagPayload) => {
  return {
    status: await createWorkspaceTag(params),
    tag: params.tagName,
  };
});
sample({
  clock: createWorkspaceTagEvent,
  target: createWorkspaceTagFx,
});
$workspaceTags.on(createWorkspaceTagFx.doneData, (state, res) => {
  if (res.status) {
    return {
      ...state,
      tags: [...state.tags, { name: res.tag }],
    };
  }
  return state;
});
sample({
  clock: createWorkspaceTagFx.doneData,
  filter: (data) => data.status,
  fn: (data) => {
    return {
      menuType: MenuType.WEBNOTES_MENU,
      item: createTagMenuItem({ name: data.tag }),
    };
  },
  target: addTagMenuItemFx,
});

// Delete workspace tag
const deleteWorkspaceTagFx = createEffect(async (params: DeleteWorkspaceTagPayload) => {
  return {
    status: await deleteWorkspaceTag(params),
    tag: params.tagName,
  };
});
sample({
  clock: deleteWorkspaceTagEvent,
  target: deleteWorkspaceTagFx,
});
sample({
  clock: deleteWorkspaceTagFx.doneData,
  source: $menuState,
  filter: (_, data) => data.status,
  fn: (menuState, data) => {
    return {
      menuType: MenuType.WEBNOTES_MENU,
      item: menuState['webnotes-menu'].entities[`${MenuItemType.TAG}#${generateTagId(data.tag)}`],
    };
  },
  target: deleteMenuItemEvent,
});
$workspaceTags.on(deleteWorkspaceTagFx.doneData, (state, res) => {
  if (res.status) {
    return {
      ...state,
      tags: state.tags.filter((t) => t.name !== res.tag),
    };
  }
  return state;
});
$notesTags.on(deleteWorkspaceTagFx.doneData, (state, res) => {
  const notesTags = Object.keys(state.notes).reduce((acc: Record<string, NoteTags>, key) => {
    const noteTags = state.notes[key];
    acc[key] = {
      ...noteTags,
      tags: noteTags.tags.filter((t) => t !== res.tag),
    };
    return acc;
  }, {} as Record<string, NoteTags>);
  const newTags = Object.fromEntries(Object.entries(state.tags).filter(([t]) => t !== res.tag));
  return {
    notes: notesTags,
    tags: newTags,
  };
});

// Rename workspace tag
const renameWorkspaceTagFx = createEffect(async (params: RenameWorkspaceTagPayload) => {
  return {
    status: await renameWorkspaceTag(params),
    oldTagName: params.tag,
    newTagName: params.newName,
  };
});
sample({
  clock: renameWorkspaceTagEvent,
  target: renameWorkspaceTagFx,
});
$notesTags.on(renameWorkspaceTagFx.doneData, (state, res) => {
  if (!res.status) {
    return state;
  }
  const notesTags = Object.keys(state.notes).reduce((acc: Record<string, NoteTags>, key) => {
    const noteTags = state.notes[key];
    acc[key] = {
      ...noteTags,
      tags: noteTags.tags.map((t) => (t === res.oldTagName ? res.newTagName : t)),
    };
    return acc;
  }, {} as Record<string, NoteTags>);
  let newTags = state.tags;
  newTags[res.newTagName] = state.tags[res.oldTagName];
  newTags = Object.fromEntries(Object.entries(state.tags).filter(([t]) => t !== res.oldTagName));
  return {
    notes: notesTags,
    tags: newTags,
  };
});
$workspaceTags.on(renameWorkspaceTagFx.doneData, (state, res) => {
  if (!res.status) {
    return state;
  }
  const filteredList = state.tags.filter((tag) => tag.name !== res.oldTagName);
  return {
    ...state,
    tags: [...filteredList, { name: res.newTagName }],
  };
});
sample({
  clock: renameWorkspaceTagFx.doneData,
  source: $menuState,
  filter: (_, data) => data.status,
  fn: (menuState, data) => {
    return {
      menuType: MenuType.WEBNOTES_MENU,
      item: menuState['webnotes-menu'].entities[`${MenuItemType.TAG}#${generateTagId(data.oldTagName)}`],
    };
  },
  target: deleteMenuItemEvent,
});
sample({
  clock: renameWorkspaceTagFx.doneData,
  filter: (data) => data.status,
  fn: (data) => {
    return {
      menuType: MenuType.WEBNOTES_MENU,
      item: createTagMenuItem({ name: data.newTagName }),
    };
  },
  target: addTagMenuItemFx,
});
sample({
  clock: addTagMenuItemFx.done,
});

// Adding tag to note
const addTagToNoteFx = createEffect(async (params: AddTagToNotePayload) => {
  const isTagNew = !$workspaceTags.getState().tags.some((tag) => tag.name === params.tag);
  return {
    ...params,
    success: await addTagToNote(params),
    isTagNew,
  };
});
$notesTags.on(startAddingTagToNoteEvent, (state, { noteId, tag }) => {
  return {
    ...state,
    notes: {
      ...state.notes,
      [noteId]: {
        ...state.notes[noteId],
        tags: [...state.notes[noteId].tags, tag],
      },
    },
  };
});
sample({
  clock: addTagToNoteEvent,
  source: [$notesTags, $currentWorkspace],
  fn: (stores, { noteId, tag }) => {
    const workspace = stores[1] as Workspace;
    return { workspaceId: workspace.globalId, noteId, tag };
  },
  filter: (stores, { noteId, tag }) => {
    const notesTags = stores[0] as NotesTagsState;
    return !notesTags.notes[noteId]?.tags.includes(tag);
  },
  target: [addTagToNoteFx, startAddingTagToNoteEvent],
});
sample({
  clock: addTagToNoteFx.doneData,
  filter: (data) => data.isTagNew,
  fn: (data) => ({
    menuType: MenuType.WEBNOTES_MENU,
    item: createTagMenuItem({ name: data.tag }),
  }),
  target: [addTagMenuItemFx],
});
$notesTags.on(addTagToNoteFx.doneData, (state, result) => {
  if (!result.success) {
    return {
      ...state,
      notes: {
        ...state.notes,
        [result.noteId]: {
          ...state.notes[result.noteId],
          tags: state.notes[result.noteId]?.tags.filter((t) => t !== result.tag),
        },
      },
    };
  }
  return state;
});
$workspaceTags.on(addTagToNoteFx.doneData, (state, result) => {
  if (!result.success || state.tags.some((tag) => tag.name === result.tag)) {
    return state;
  }
  return {
    ...state,
    tags: [...state.tags, { name: result.tag }],
  };
});

// Deleting tag from note
export const deleteTagFromNoteFx = createEffect(async (params: DeleteTagFromNotePayload) => {
  return {
    ...params,
    success: await deleteTagFromNote(params),
  };
});

$notesTags.on(startDeletingTagFromNoteEvent, (state, { noteId, tag }) => {
  return {
    ...state,
    notes: {
      ...state.notes,
      [noteId]: {
        ...state.notes[noteId],
        tags: [...state.notes[noteId].tags.filter((t) => t !== tag)],
      },
    },
  };
});

sample({
  clock: deleteTagFromNoteEvent,
  source: [$notesTags, $currentWorkspace],
  fn: (stores, { noteId, tag }) => {
    const workspace = stores[1] as Workspace;
    return { workspaceId: workspace.globalId, noteId, tag };
  },
  filter: (stores, { noteId, tag }) => {
    const notesTags = stores[0] as NotesTagsState;
    return notesTags.notes[noteId]?.tags.includes(tag);
  },
  target: [deleteTagFromNoteFx, startDeletingTagFromNoteEvent],
});

$notesTags.on(deleteTagFromNoteFx.doneData, (state, result) => {
  if (!result.success) {
    return {
      ...state,
      notes: {
        ...state.notes,
        [result.noteId]: {
          ...state.notes[result.noteId],
          tags: [...(state.notes[result.noteId]?.tags ?? []), result.tag],
        },
      },
    };
  }
  return state;
});

// Fetching workspace tags
const fetchWorkspaceTagsFx = createEffect(getWorkspaceTags);

sample({
  clock: fetchWorkspaceTagsEvent,
  target: fetchWorkspaceTagsFx,
});

$workspaceTags.on(fetchWorkspaceTagsFx.doneData, (state, tags) => {
  return {
    ...state,
    tags: tags ?? [],
    isFetching: false,
  };
});

// Fetching workspace notes' tags
const fetchWorkspaceNotesTagsFx = createEffect(async (params: { workspaceId: string }) => {
  return {
    workspaceId: params.workspaceId,
    notes: await getWorkspaceNotesTags({ workspaceId: params.workspaceId }),
  };
});

sample({
  clock: fetchWorkspaceNotesTagsEvent,
  source: $currentWorkspace,
  fn: (currentWorkspace: Workspace) => {
    return { workspaceId: currentWorkspace.globalId };
  },
  target: fetchWorkspaceNotesTagsFx,
});

$notesTags.on(fetchWorkspaceNotesTagsFx.doneData, (state, result) => {
  const notesTags = result.notes ?? [];
  const allTags: Record<string, string[]> = {};
  const allNotes: Record<string, NoteTags> = {};
  for (const { tags, noteGlobalId } of notesTags) {
    allNotes[noteGlobalId] = {
      isFetching: false,
      noteId: noteGlobalId,
      tags,
    };
    for (const tag of tags) {
      if (!allTags[tag]) {
        allTags[tag] = [];
      }
      allTags[tag].push(noteGlobalId);
    }
  }

  return {
    ...state,
    notes: {
      ...state.notes,
      ...allNotes,
    },
    tags: allTags,
  };
});
